import {
  EmbeddedAuthError,
  type EmbeddedAuthTokenResult,
} from '@/platform/authentication/MsalPopupAuthService';
import { tokenProvider } from '@/platform/authentication/TokenProvider';
import type {
  EmbeddedAuthErrorPayload,
  EmbeddedAuthRequestPayload,
  EmbeddedAuthStatePayload,
} from '@/types/embeddedApplication';

function toAuthStatePayload(
  request: EmbeddedAuthRequestPayload,
  tokenResult: EmbeddedAuthTokenResult,
): EmbeddedAuthStatePayload {
  return {
    version: 1,
    correlationId: request.correlationId,
    productId: request.productId,
    accessToken: tokenResult.accessToken,
    expiresAt: tokenResult.expiresAt,
    scopes: tokenResult.scopes,
    account: tokenResult.account,
  };
}

function toAuthErrorPayload(
  request: EmbeddedAuthRequestPayload,
  error: EmbeddedAuthError,
): EmbeddedAuthErrorPayload {
  return {
    version: 1,
    correlationId: request.correlationId,
    productId: request.productId,
    code: error.code,
    message: error.message,
    retryable: error.retryable,
    popupBlocked: error.popupBlocked,
  };
}

export class EmbeddedAuthService {
  async authenticate(
    request: EmbeddedAuthRequestPayload,
    options: { interactive?: boolean } = {},
  ): Promise<EmbeddedAuthStatePayload> {
    const tokenResult = await tokenProvider.getTokenForProduct(
      request.productId,
      request.requestedScopes,
      {
        interactive: options.interactive,
      },
    );

    return toAuthStatePayload(request, tokenResult);
  }

  async refresh(
    request: EmbeddedAuthRequestPayload,
  ): Promise<EmbeddedAuthStatePayload> {
    const tokenResult = await tokenProvider.getTokenForProduct(
      request.productId,
      request.requestedScopes,
      {
        interactive: false,
      },
    );

    return toAuthStatePayload(request, tokenResult);
  }

  clearSession(productId: string): void {
    tokenProvider.clearProductSession(productId);
  }

  toErrorPayload(
    request: EmbeddedAuthRequestPayload,
    error: unknown,
  ): EmbeddedAuthErrorPayload {
    if (error instanceof EmbeddedAuthError) {
      return toAuthErrorPayload(request, error);
    }

    const unknownError = new EmbeddedAuthError(
      'unknown',
      error instanceof Error ? error.message : 'Authentication failed.',
      true,
    );

    return toAuthErrorPayload(request, unknownError);
  }
}

export const embeddedAuthService = new EmbeddedAuthService();
